import React, { FC } from 'react';
import BatchBlockHeader from './components/BatchBlockHeader';
import BlockWrapper from '@v2/pages/machines/modules/now/components/Overview/components/BlockWrapper';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { useManageBatchBlock } from './hooks/useManageBatchBlock';
import BatchBlockTime from './components/BatchBlockTime';
import { scale } from '@components/frame';
import {
  BATCH_BLOCK_CONFIG,
  ACTIVE_BULLET_ANIMATION_CONFIG,
  GAP_BETWEEN_TIME_BLOCKS,
} from '@v2/pages/machines/modules/now/components/Overview/constants';
import BatchTimelineWithLegends from './components/BatchTimelineWithLegends';
import ReadyPcsV2, { ReadyPcsV2Props } from '@components/common/ReadyPcsV2';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import { InfoColumn } from '@v2/pages/machines/modules/now/components/Overview/components/InfoColumn';
import ColorWrapper, { WithColor } from '@styles/colors';
import { font } from '@styles/fonts';
import { injectConditionalObject, isPhone } from '@helpers';
import { role } from '@components/frame';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface BatchBlockProps extends WithColor {}

const BatchBlock: FC<BatchBlockProps> = ({ C }) => {
  const {
    onBatchBlockPress,
    batch,
    startedTime,
    targetTime,
    isBatchAvailable,
    batchLoaded,
    batchNotAvailableText,
    endTimeText,
    colorBasedOnMachineStatus,
    shouldActiveBulletBeAnimated,
    shouldDisplayEndTime,
    longestEndTimeText,
  } = useManageBatchBlock({ C });

  const isReadOnly = isRestrictedForOperator(role, RESTRICTION_CATEGORIES.LIVE_OVERVIEW_READ_ONLY, 'BatchBlock');

  const readyPcsV2Props: Omit<ReadyPcsV2Props, 'C'> = {
    colorForQuantityProduced: 'Label/Light/Primary',
    readyLabelFont: 'Footnote',
    containerStyle: isPhone ? { flex: 1 } : { flexBasis: '33%', flexShrink: 0, flexGrow: 0 },
    quantityContainerStyle: { marginTop: scale(-1) },
    batch,
    activeBulletConfig: {
      animationConfig: {
        color: colorBasedOnMachineStatus,
        isActive: shouldActiveBulletBeAnimated,
        ...ACTIVE_BULLET_ANIMATION_CONFIG,
      },
    },
  } as const;

  const { height, paddingVertical } = BATCH_BLOCK_CONFIG;

  return (
    <TourTarget tourId={WELCOME_TOUR_IDS.LIVE_BATCH_BLOCK}>
      <BlockWrapper
        testID={TEST_IDS.liveScreen.batchBlock}
        onPress={batchLoaded && !isReadOnly ? onBatchBlockPress : undefined}
        containerStyle={{ paddingVertical: scale(paddingVertical), height: scale(height) }}
      >
        {!isBatchAvailable && (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text style={[font.Headline, { color: C('Label/Light/Tertiary') }]}>{batchNotAvailableText}</Text>
          </View>
        )}

        {isBatchAvailable && (
          <>
            <BatchBlockHeader containerStyle={{ height: scale(23), marginBottom: scale(4) }} />
            <BatchBlockTime />
            <BatchTimelineWithLegends containerStyle={{ marginTop: scale(16) }} />
            <View style={{ flexDirection: 'row', marginTop: scale(16), flex: 1 }}>
              {/* Phone layout */}
              {isPhone && (
                <>
                  <ReadyPcsV2 {...readyPcsV2Props} />
                  <View style={{ flex: 1, alignItems: 'center' }}>
                    <InfoColumn containerStyle={{ flex: 1 }} label={i18n.t('STARTED')} C={C} value={startedTime} valueColor="Label/Light/Primary" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <InfoColumn
                      containerStyle={{ flex: 1, marginLeft: 'auto', ...injectConditionalObject(!shouldDisplayEndTime, { opacity: 0 }) }}
                      label={endTimeText}
                      longestLabel={longestEndTimeText}
                      C={C}
                      value={targetTime}
                      valueColor={'Label/Light/Primary'}
                    />
                  </View>
                </>
              )}

              {/* Web/Tablet layout */}
              {!isPhone && (
                <>
                  <ReadyPcsV2 {...readyPcsV2Props} />
                  <View style={{ flex: 1 }} />
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(GAP_BETWEEN_TIME_BLOCKS) }}>
                    <InfoColumn label={i18n.t('STARTED')} C={C} value={startedTime} valueColor={'Label/Light/Primary'} />
                    <InfoColumn
                      containerStyle={{
                        ...injectConditionalObject(!shouldDisplayEndTime, { opacity: 0 }),
                      }}
                      label={endTimeText}
                      longestLabel={longestEndTimeText}
                      C={C}
                      value={targetTime}
                      valueColor={'Label/Light/Primary'}
                    />
                  </View>
                </>
              )}
            </View>
          </>
        )}
      </BlockWrapper>
    </TourTarget>
  );
};

export default ColorWrapper(BatchBlock);
